import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-[60px] h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/1273c1d0-4f13-4a52-8152-0be9d81b4214.png" 
              alt="MOOG SARL Logo" 
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-2xl font-bold text-[#2C2C2C]">MOOG SARL</h1>
          </div>
          <nav className="flex items-center gap-10">
            <button
              onClick={() => scrollToSection('products')}
              className="text-sm font-medium text-[#4A4A4A] hover:text-[#2D5F4F] transition-colors uppercase tracking-wide cursor-pointer whitespace-nowrap"
            >
              Products
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="text-sm font-medium text-[#4A4A4A] hover:text-[#2D5F4F] transition-colors uppercase tracking-wide cursor-pointer whitespace-nowrap"
            >
              Technology
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-sm font-medium text-[#4A4A4A] hover:text-[#2D5F4F] transition-colors uppercase tracking-wide cursor-pointer whitespace-nowrap"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-sm font-medium text-[#4A4A4A] hover:text-[#2D5F4F] transition-colors uppercase tracking-wide cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-[85vh] flex pt-20">
        <div className="w-[45%] bg-[#F8F6F3] flex items-center justify-center px-16">
          <div className="max-w-[540px]">
            <div className="text-[11px] uppercase tracking-[0.15em] text-[#2D5F4F] font-semibold mb-8">
              iOS Development Experts
            </div>
            <h2 className="font-serif text-[72px] leading-[1.1] text-[#1A1A1A] mb-12" style={{ letterSpacing: '-0.5px' }}>
              Crafting Premium<br />
              iOS Applications<br />
              for Modern Users
            </h2>
            <p className="text-lg text-[#6B6B6B] leading-[1.6] max-w-[480px]">
              Expert team developing cutting-edge VPN and Cleaner solutions that prioritize security, performance, and user experience
            </p>
          </div>
        </div>
        <div className="w-[55%] bg-gradient-to-br from-[#E8E3F3] to-[#E3F0F8] flex items-center justify-center relative overflow-hidden">
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/cf59721068a66e3f34fd27c7dfdfe860.jpg"
              alt="VPN App Interface"
              className="w-[400px] h-[800px] object-contain transform rotate-[15deg] drop-shadow-[0_40px_80px_rgba(0,0,0,0.12)]"
            />
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-white py-[140px]">
        <div className="max-w-[1400px] mx-auto px-[60px]">
          <div className="flex items-start gap-2 mb-6">
            <div className="w-2 h-2 rounded-full bg-[#2D5F4F] mt-2"></div>
            <div className="text-[13px] uppercase tracking-[0.12em] text-[#2D5F4F] font-semibold">
              Our Products
            </div>
          </div>
          <h2 className="font-serif text-[64px] leading-[1.1] text-[#1A1A1A] mb-[100px] max-w-[500px]">
            Applications That<br />
            Enhance Your<br />
            Digital Life
          </h2>

          <div className="grid grid-cols-3 gap-8">
            {/* VPN Product */}
            <div className="group bg-white border-2 border-[#E5E5E5] rounded-3xl overflow-hidden hover:border-transparent hover:shadow-[0_0_0_2px_#2D5F4F] hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="h-[420px] bg-white flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20VPN%20mobile%20application%20screenshot%20showing%20secure%20connection%20interface%20with%20shield%20protection%20icon%2C%20modern%20iOS%20design%20with%20green%20accent%20colors%2C%20clean%20white%20background%2C%20connection%20status%20display%2C%20server%20selection%20menu%2C%20security%20features%20visible%2C%20premium%20app%20UI%20design%2C%20minimalist%20layout%20with%20elegant%20typography&width=300&height=400&seq=vpn-product-screenshot-001&orientation=portrait"
                  alt="VPN Application"
                  className="w-full h-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.06)]"
                />
              </div>
              <div className="p-8">
                <h3 className="font-serif text-[28px] text-[#1A1A1A] mb-2">VPN</h3>
                <div className="text-sm text-[#2D5F4F] mb-3 font-medium">Security Application</div>
                <p className="text-[15px] text-[#757575] leading-[1.5]">
                  Secure your online presence with military-grade encryption and global server access
                </p>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="group bg-white border-2 border-[#E5E5E5] rounded-3xl overflow-hidden hover:border-transparent hover:shadow-[0_0_0_2px_#2D5F4F] hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="h-[420px] bg-white flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20iOS%20Cleaner%20application%20screenshot%20displaying%20storage%20optimization%20interface%20with%20sparkle%20cleaning%20icon%2C%20elegant%20mobile%20UI%20showing%20storage%20analysis%20charts%2C%20file%20management%20system%2C%20clean%20white%20background%20with%20blue%20and%20green%20accents%2C%20premium%20app%20design%2C%20minimalist%20layout%20with%20progress%20indicators%20and%20storage%20categories&width=300&height=400&seq=cleaner-product-screenshot-002&orientation=portrait"
                  alt="Cleaner Application"
                  className="w-full h-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.06)]"
                />
              </div>
              <div className="p-8">
                <h3 className="font-serif text-[28px] text-[#1A1A1A] mb-2">Cleaner</h3>
                <div className="text-sm text-[#2D5F4F] mb-3 font-medium">Optimization Tool</div>
                <p className="text-[15px] text-[#757575] leading-[1.5]">
                  Optimize device performance by removing junk files and managing storage efficiently
                </p>
              </div>
            </div>

            {/* Future Product */}
            <div className="group bg-white border-2 border-[#E5E5E5] rounded-3xl overflow-hidden hover:border-transparent hover:shadow-[0_0_0_2px_#2D5F4F] hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="h-[420px] bg-gradient-to-br from-[#E8E3F3] to-[#E3F0F8] flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-white/50 backdrop-blur-sm flex items-center justify-center">
                    <i className="ri-apps-line text-5xl text-[#2D5F4F]"></i>
                  </div>
                  <div className="text-[#4A4A4A] text-lg font-medium">Coming Soon</div>
                </div>
              </div>
              <div className="p-8">
                <h3 className="font-serif text-[28px] text-[#1A1A1A] mb-2">Next Innovation</h3>
                <div className="text-sm text-[#2D5F4F] mb-3 font-medium">In Development</div>
                <p className="text-[15px] text-[#757575] leading-[1.5]">
                  We are working on exciting new features to enhance your mobile experience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-[#FAFAFA] py-[160px]">
        <div className="max-w-[1400px] mx-auto px-[60px]">
          <div className="flex gap-16">
            <div className="w-[40%] pl-20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-[3px] h-10 bg-[#2D5F4F]"></div>
                <div className="text-[13px] uppercase tracking-[0.12em] text-[#2D5F4F] font-semibold">
                  Technology
                </div>
              </div>
              <h2 className="font-serif text-[58px] leading-[1.1] text-[#1A1A1A] mb-8">
                Built With<br />
                Precision &<br />
                Performance
              </h2>
              <p className="text-[17px] text-[#6B6B6B] leading-[1.7] max-w-[420px]">
                Our applications are crafted using the latest iOS technologies and best practices. We prioritize code quality, security standards, and optimal performance to deliver exceptional user experiences across all Apple devices.
              </p>
            </div>

            <div className="w-[60%] grid grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-12 flex flex-col items-center justify-center text-center hover:scale-105 transition-transform duration-200 cursor-pointer">
                <i className="ri-code-s-slash-line text-5xl text-[#2D5F4F] mb-4"></i>
                <div className="text-sm font-medium text-[#4A4A4A]">Swift</div>
              </div>
              <div className="bg-white rounded-2xl p-12 flex flex-col items-center justify-center text-center hover:scale-105 transition-transform duration-200 cursor-pointer">
                <i className="ri-shield-check-line text-5xl text-[#2D5F4F] mb-4"></i>
                <div className="text-sm font-medium text-[#4A4A4A]">Security</div>
              </div>
              <div className="bg-white rounded-2xl p-12 flex flex-col items-center justify-center text-center hover:scale-105 transition-transform duration-200 cursor-pointer">
                <i className="ri-cloud-line text-5xl text-[#2D5F4F] mb-4"></i>
                <div className="text-sm font-medium text-[#4A4A4A]">Cloud</div>
              </div>
              <div className="bg-white rounded-2xl p-12 flex flex-col items-center justify-center text-center hover:scale-105 transition-transform duration-200 cursor-pointer">
                <i className="ri-line-chart-line text-5xl text-[#2D5F4F] mb-4"></i>
                <div className="text-sm font-medium text-[#4A4A4A]">Analytics</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white py-[200px]">
        <div className="max-w-[900px] mx-auto px-[60px]">
          <div className="text-[12px] uppercase tracking-[0.12em] text-[#6B8E7F] font-semibold mb-8">
            About Us
          </div>
          <h2 className="font-serif text-[68px] leading-[1.2] text-[#1F4037] mb-[120px]">
            We Are MOOG SARL,<br />
            A Team of Experts<br />
            Developing Modern<br />
            Applications for iOS
          </h2>
          <p className="text-[19px] text-[#5A5A5A] leading-[1.65] max-w-[720px]">
            Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine technical expertise with user-centered design to create applications that make a real difference in people's digital lives.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="bg-[#1F3A2F] text-white">
        <div className="max-w-[1400px] mx-auto px-[60px] pt-[100px] pb-[80px]">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/1273c1d0-4f13-4a52-8152-0be9d81b4214.png" 
                  alt="MOOG SARL Logo" 
                  className="h-8 w-8 object-contain"
                />
                <div className="text-2xl font-bold">MOOG SARL</div>
              </div>
              <div className="space-y-2 text-sm text-[#B8C9C0] leading-4">
                <div>Premium iOS applications</div>
                <div>Security-focused solutions</div>
                <div>Modern development practices</div>
              </div>
            </div>

            {/* Newsletter Column */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#9FB5A8] mb-4 font-semibold">
                Stay Updated
              </div>
              <div className="relative">
                <input
                  type="email"
                  placeholder="Your email"
                  className="w-full bg-transparent border-b border-[#5A7A6A] h-10 text-white placeholder-[#8FA89D] focus:outline-none focus:border-[#B8C9C0] transition-colors pr-10"
                />
                <button className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity whitespace-nowrap">
                  <i className="ri-arrow-right-line text-xl text-[#B8C9C0]"></i>
                </button>
              </div>
              <div className="text-[13px] text-[#8FA89D] mt-2">Subscribe to our newsletter</div>
            </div>

            {/* Quick Links Column */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#9FB5A8] mb-4 font-semibold">
                Company
              </div>
              <div className="space-y-4">
                <button
                  onClick={() => scrollToSection('about')}
                  className="block text-white hover:underline cursor-pointer whitespace-nowrap"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('products')}
                  className="block text-white hover:underline cursor-pointer whitespace-nowrap"
                >
                  Products
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  className="block text-white hover:underline cursor-pointer whitespace-nowrap"
                >
                  Technology
                </button>
              </div>
            </div>

            {/* Contact Column */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#9FB5A8] mb-4 font-semibold">
                Contact
              </div>
              <div className="space-y-3 text-[15px] leading-6">
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-lg mt-0.5 flex-shrink-0"></i>
                  <div>
                    <div>1 CHE DE LAPORTE</div>
                    <div>TOULOUSE 31300</div>
                    <div>France</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-lg flex-shrink-0"></i>
                  <a href="mailto:contact@moogsarl.com" className="hover:underline cursor-pointer whitespace-nowrap">
                    contact@moogsarl.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-lg flex-shrink-0"></i>
                  <a href="tel:+16188003529" className="hover:underline cursor-pointer whitespace-nowrap">
                    +1 6188003529
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Large Branding */}
        <div className="relative h-[240px] overflow-hidden">
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[180px] font-bold text-[#E8DCC8] opacity-50 whitespace-nowrap" style={{ letterSpacing: '-4px' }}>
            MOOG
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#5A7A6A]/30 py-6">
          <div className="max-w-[1400px] mx-auto px-[60px] flex justify-between items-center text-sm text-[#8FA89D]">
            <div>&copy; 2025 MOOG SARL. All rights reserved.</div>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
